import { SpineTexture, TextureAtlas } from '@esotericsoftware/spine-pixi-v8';
import {
  AssetExtension,
  Assets,
  checkExtension,
  copySearchParams,
  DOMAdapter,
  ExtensionType,
  LoaderParserPriority,
  path,
  ResolvedAsset,
  Resolver,
  Texture,
  TextureSource,
  UnresolvedAsset
} from 'pixi.js';

/* eslint-disable */
// Disabling eslint due to this being a pixi extension which doesn't necessarily follow our standards.

type RawAtlas = string;
const loaderName = "CustomSpineTextureAtlasLoader";

export type ExtendedTextureAtlas = TextureAtlas & { rawText: string };

export interface ISpineAtlasMetadata {
  // If you are downloading an .atlas file, this metadata will go to the Texture loader
  imageMetadata?: unknown;
  // If you already have atlas pages loaded as pixi textures and want to use that to create the atlas, you can pass them here
  images?: TextureSource | string | Record<string, TextureSource | string>;
}

export const customSpineTextureAtlasLoader: AssetExtension<RawAtlas | TextureAtlas, ISpineAtlasMetadata> = {
  extension: ExtensionType.Asset,

  resolver: {
		test: (value: string): boolean => checkExtension(value, ".atlas"),
		parse: (value: string): UnresolvedAsset => {
			const split = value.split('.');

			return {
				resolution: parseFloat(Resolver.RETINA_PREFIX?.exec(value)?.[1] ?? '1'),
				format: split[split.length - 2],
				src: value,
			};
		},
	},

  loader: {
    name: loaderName,
    id: loaderName,

    extension: {
      type: ExtensionType.LoadParser,
      priority: LoaderParserPriority.High,
      name: loaderName
    },

    test(url: string): boolean {
      return checkExtension(url, '.atlas');
    },

    async load(url: string): Promise<RawAtlas> {
      const response = await DOMAdapter.get().fetch(url);

      const txt = await response.text();

      return txt;
    },

		testParse (asset: unknown, options: ResolvedAsset): Promise<boolean> {
			const isExtensionRight = checkExtension(options.src as string, '.atlas');
			const isString = typeof asset === 'string';
			const isExplicitLoadParserSet = options.parser === loaderName || options.loadParser === loaderName;

			return Promise.resolve((isExtensionRight || isExplicitLoadParserSet) && isString);
    },

    unload(atlas: TextureAtlas) {
      atlas.dispose();
    },

    async parse(asset: RawAtlas, options: ResolvedAsset<ISpineAtlasMetadata>): Promise<TextureAtlas> {
      const metadata: ISpineAtlasMetadata = options.data || {};
      let basePath = path.dirname(options.src as string);

      if (basePath && basePath.lastIndexOf('/') !== basePath.length - 1) {
        basePath += '/';
      }

      // Retval is going to be a texture atlas. However we need to wait for it's callback to resolve this promise.
      const retval = new TextureAtlas(asset) as ExtendedTextureAtlas;
      retval.rawText = asset;

      // If the user gave me only one texture, that one is assumed to be the "first" texture in the atlas
      if (metadata.images instanceof TextureSource || typeof metadata.images === 'string') {
        const pixiTexture = metadata.images;
        metadata.images = {} as Record<string, TextureSource | string>;
        metadata.images[retval.pages[0].name] = pixiTexture;
      }

      // we will wait for all promises for the textures at the same time at the end.
			const textureLoadingPromises: Promise<any>[] = [];

      // fill the pages
      for (const page of retval.pages) {
        const pageName = page.name;
        const providedPage = metadata?.images ? metadata.images[pageName] : undefined;
        if (providedPage instanceof TextureSource) {
          page.setTexture(SpineTexture.from(providedPage));
        } else {
          const url: string = providedPage ?? path.normalize([...basePath.split(path.sep), pageName].join(path.sep));
          
          const assetsToLoadIn = {
            alias: [pageName],
						src: copySearchParams(url, options.src as string),
						data: {
							...(metadata.imageMetadata as Record<string, unknown>),
							alphaMode: page.pma ? 'premultiplied-alpha' : 'premultiply-alpha-on-upload'
						}
					};
          
          const pixiPromise = Assets.load<Texture>(assetsToLoadIn).then((texture) => {
            page.setTexture(SpineTexture.from(texture.source));
          });
          textureLoadingPromises.push(pixiPromise);
        }
      }

      if (textureLoadingPromises.length !== 0) {
        await Promise.all(textureLoadingPromises);
      }

      return retval;
    }
  }
} as AssetExtension<RawAtlas | TextureAtlas, ISpineAtlasMetadata>;
